function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import { useDrag } from "@use-gesture/react";
import styled from "styled-components";
import React, { memo, useCallback, useRef } from "react";
import clamp from "astrid-helpers/src/clamp";
import map from "astrid-helpers/src/map";
import volumeToDecibel from "astrid-helpers/src/volumeToDecibel";
import useTemp from "astrid-hooks/src/useTemp";
import { primaryColor, secondaryColor } from "../../../helpers/color";
import { transparentizedPrimaryColor } from "../../../helpers/transparentize";
import MouseLabel from "../../Modules/MouseLabel/MouseLabel";
import { getRect } from "./state/rect";
import { useScope } from "./state/scope";
import { getSize } from "./state/size";
const Root = /*#__PURE__*/styled.div.withConfig({
  displayName: "Root",
  componentId: "sc-ltoxkr-0"
})(["position:absolute;z-index:2;bottom:0;width:100%;pointer-events:none;"]);
const Handle = /*#__PURE__*/memo(styled.div.withConfig({
  displayName: "Handle",
  componentId: "sc-ltoxkr-1"
})(["position:absolute;top:-3px;left:50%;width:10px;height:10px;border-radius:100%;background-color:", ";transform:translateX(-50%);pointer-events:all;cursor:move;&::before{content:\"\";position:absolute;top:-10px;left:-10px;right:-10px;bottom:-10px;}&::after{content:\"\";position:absolute;top:-5px;left:-5px;width:20px;height:20px;border-radius:100%;background-color:", ";transform:scale(0.5);transition:transform 0.2s ease-in-out;}&:hover::after{transform:scale(1);}"], primaryColor, transparentizedPrimaryColor));
function Path({
  style,
  className,
  fadein,
  fadeout,
  ...props
}) {
  const color = secondaryColor(props);
  return /*#__PURE__*/React.createElement("svg", {
    width: "100",
    height: "100",
    style: style,
    version: "1.1",
    viewBox: "0 0 100 100",
    className: className,
    preserveAspectRatio: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    stroke: color,
    strokeWidth: "2",
    fill: "transparent",
    vectorEffect: "non-scaling-stroke",
    d: `M0,100 C0,100 0,1 ${fadein * 100},1 L${(1 - fadeout) * 100},1 C100,1 100,100 100,100`
  }));
}
const StyledPath = /*#__PURE__*/memo(styled(Path).attrs(({
  theme
}) => ({
  theme
})).withConfig({
  displayName: "StyledPath",
  componentId: "sc-ltoxkr-2"
})(["position:absolute;left:0;width:100%;height:100%;"]));
const Volume = /*#__PURE__*/memo(({
  end,
  start,
  volume,
  fadein,
  fadeout,
  selected,
  onChangeVolume,
  onChangeFadein,
  onChangeFadeout
}) => {
  const scope = useScope();
  const state = useRef();
  const [tempVolume, setTempVolume, getTempVolume] = useTemp(volume);
  const [tempFadein, setTempFadein, getTempFadein] = useTemp(fadein);
  const [tempFadeout, setTempFadeout, getTempFadeout] = useTemp(fadeout);
  const length = end - start;
  const onDrag = useDrag(useCallback(({
    args,
    axis,
    last,
    first,
    event,
    movement
  }) => {
    event.stopPropagation();
    if (first) {
      state.current = args[0];
    } else if (last) {
      MouseLabel.setLabel();
      if (onChangeVolume && axis === "y") {
        onChangeVolume(getTempVolume());
      } else {
        if (onChangeFadein && state.current === "fade in") {
          onChangeFadein(getTempFadein());
        } else if (onChangeFadeout && state.current === "fade out") {
          onChangeFadeout(getTempFadeout());
        }
      }
    } else {
      switch (axis) {
        case "y":
          if (state.current === "volume") {
            const tempVolume = Math.round(clamp(volume + movement[1] * -0.015, 0.0005, 2.3) * 10000) / 10000;
            setTempVolume(tempVolume);
            MouseLabel.setLabel(`${volumeToDecibel(tempVolume)} dB`);
          }
          break;
        case "x":
          const {
            width
          } = getRect();
          const diff = map(movement[0], 0, width, 0, getSize());
          if (state.current === "fade in") {
            const tempFadein = Math.round(clamp(fadein + diff, 0, 2000));
            setTempFadein(tempFadein);
          } else if (state.current === "fade out") {
            const tempFadeout = Math.round(clamp(fadeout - diff, 0, 2000));
            setTempFadeout(tempFadeout);
          }
          break;
        default:
      }
    }
  }, [fadein, fadeout, getTempFadein, getTempFadeout, getTempVolume, onChangeFadein, onChangeFadeout, onChangeVolume, setTempFadein, setTempFadeout, setTempVolume, volume]));
  if (scope.length < 3000) return false;
  return /*#__PURE__*/React.createElement(Root, _extends({
    style: {
      height: `${Math.sqrt(tempVolume) * 66}%`
    }
  }, onDrag("volume")), /*#__PURE__*/React.createElement(StyledPath, {
    fadein: tempFadein / length,
    fadeout: tempFadeout / length
  }), selected && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MouseLabel.Trigger, {
    label: `${volumeToDecibel(tempVolume)} dB`
  }, /*#__PURE__*/React.createElement(Handle, onDrag("volume"))), /*#__PURE__*/React.createElement(Handle, _extends({
    style: {
      left: `${tempFadein / length * 100}%`
    }
  }, onDrag("fade in"))), /*#__PURE__*/React.createElement(Handle, _extends({
    style: {
      left: `${(1 - tempFadeout / length) * 100}%`
    }
  }, onDrag("fade out")))));
});
export default Volume;
//# sourceMappingURL=Volume.js.map